<script setup lang="ts">
import type { AppResponseListChatSessionResponse } from "@agent-service-types/index.ts";
import { keepPreviousData } from "@tanstack/vue-query";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";
import { useExchangeMode } from "@/composables/useExchangeMode";
import { useGetChatSessions } from "@/providers/chatSessions/useGetChatSessions";
import { useUpdateChatSession } from "@/providers/chatSessions/useUpdateChatSession";
import AdvisingSessions from "./AdvisingSessions/index.vue";
import { SESSIONS_PAGE_LIMIT } from "./constants";
import type { AdvisingSession, IndexProps, SessionQueryResult } from "./types";
import { formatSessionDate, toAdvisingSession } from "./utils";

// ── Props ────────────────────────────────────────────────────────────────────

const props = withDefaults(defineProps<IndexProps>(), {});

// ── Pagination state ─────────────────────────────────────────────────────────

const offset = ref<number>(0);

const params = computed(() => ({
  userOktaId: props.userOktaId,
  limit: SESSIONS_PAGE_LIMIT,
  offset: offset.value,
}));

// ── Data fetching ─────────────────────────────────────────────────────────────

const {
  data,
  isPending: loading,
  isFetching,
  isError,
  error,
} = useGetChatSessions<SessionQueryResult>(params, {
  select: (response: AppResponseListChatSessionResponse): SessionQueryResult => ({
    sessions: (response.data ?? []).map(toAdvisingSession),
    total: response.total ?? 0,
  }),
  placeholderData: keepPreviousData,
});

const isUnauthorized = computed<boolean>(() => {
  const status = (error.value as any)?.response?.status;
  return status === 401 || status === 403;
});

const titleOverrides = ref<Record<string, string>>({});

const sessions = computed<AdvisingSession[]>(() =>
  (data.value?.sessions ?? []).map((s) =>
    s.id in titleOverrides.value ? { ...s, title: titleOverrides.value[s.id] } : s,
  ),
);
const total = computed<number>(() => data.value?.total ?? 0);

// ── Most recent session — captured once, lives for the lifetime of the component

const mostRecentSession = ref<AdvisingSession | undefined>(undefined);

watch(
  sessions,
  (newSessions) => {
    if (!mostRecentSession.value && newSessions.length > 0) {
      mostRecentSession.value = newSessions[0];
    }
  },
  { immediate: true },
);

// On first page the most recent is sessions[0] — skip it in the previous list
const previousSessions = computed<AdvisingSession[]>(() =>
  offset.value === 0 ? sessions.value.slice(1) : sessions.value,
);

const previousTotal = computed<number>(() => Math.max(0, total.value - 1));
const previousFetching = computed<boolean>(
  () => (isFetching.value && !!mostRecentSession.value) || isTitleUpdating.value,
);

// ── Permissions ──────────────────────────────────────────────────────────────

const { userIsOutboundStudentManager } = useExchangeMode();

// ── Mutations ────────────────────────────────────────────────────────────────

const toast = useToast();
const { mutate: updateSession, isPending: isTitleUpdating } = useUpdateChatSession();

// ── Handlers ─────────────────────────────────────────────────────────────────

const onPageChange = (newOffset: number): void => {
  offset.value = newOffset;
};

const onUpdateTitle = (sessionId: string, title: string): void => {
  const previousTitle = mostRecentSession.value?.id === sessionId ? mostRecentSession.value.title : undefined;

  // Optimistic updates — covers both mostRecentSession and previous sessions list
  titleOverrides.value = { ...titleOverrides.value, [sessionId]: title };
  if (mostRecentSession.value?.id === sessionId) {
    mostRecentSession.value = { ...mostRecentSession.value, title };
  }

  updateSession(
    { chatSessionId: sessionId, payload: { title } },
    {
      onSuccess: () => {
        // Query is already invalidated by useUpdateChatSession; drop the override
        // so fresh server data takes over once the re-fetch settles
        const { [sessionId]: _, ...rest } = titleOverrides.value;
        titleOverrides.value = rest;
        toast.add({
          severity: "success",
          group: "br-ads",
          summary: "Title updated",
          detail: "Session title has been saved.",
          life: 3000,
        });
      },
      onError: () => {
        const { [sessionId]: _, ...rest } = titleOverrides.value;
        titleOverrides.value = rest;
        if (previousTitle !== undefined && mostRecentSession.value?.id === sessionId) {
          mostRecentSession.value = { ...mostRecentSession.value!, title: previousTitle };
        }
        toast.add({
          severity: "error",
          group: "br-ads",
          summary: "Update failed",
          detail: "Could not save the session title. Please try again.",
          life: 5000,
        });
      },
    },
  );
};
</script>

<template>
  <div class="w-full">
    <Toast position="bottom-right" group="br-ads" />
    <div v-if="isUnauthorized" class="px-4 py-5 flex items-start gap-3">
      <span class="material-symbols-outlined text-yellow-500 text-xl leading-none mt-0.5">lock</span>
      <div>
        <p class="text-sm font-semibold text-gray-800">Access denied</p>
        <p class="text-sm text-gray-600 mt-1">You don't have permission to view this student's advising sessions.</p>
      </div>
    </div>
    <AdvisingSessions
      v-else
      :loading="loading"
      :error="isError"
      :previousFetching="previousFetching"
      :mostRecentSession="mostRecentSession"
      :sessions="previousSessions"
      :offset="offset"
      :limit="SESSIONS_PAGE_LIMIT"
      :total="previousTotal"
      :canEditTitle="userIsOutboundStudentManager"
      @update-title="onUpdateTitle"
      @page-change="onPageChange"
    />
  </div>
</template>
